import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination, Autoplay } from 'swiper/modules';
import { Quote, Star } from 'lucide-react';
import { motion } from 'framer-motion';

const reviews = [
  {
    name: "Ali Khan",
    role: "E-commerce Store Owner",
    review: "Momin developed our online store with seamless functionality and beautiful design. The conversion rate increased by 40% after launch!",
    project: "E-commerce Website",
    rating: 5
  },
  {
    name: "Zara Shah",
    role: "Fashion Boutique Owner",
    review: "Our website was delivered in just 5 days with all the features we requested. The attention to detail was exceptional.",
    project: "Fashion E-commerce Platform",
    rating: 5
  },
  {
    name: "Bilal Ahmed",
    role: "Startup CEO",
    review: "Momin understood our complex requirements and delivered a scalable solution that's both user-friendly and technically robust.",
    project: "SaaS Application",
    rating: 5
  },
  {
    name: "Ayesha Noor",
    role: "Marketing Director",
    review: "The SEO optimization work Momin did helped us rank on the first page of Google within 2 months. Highly recommended!",
    project: "Corporate Website + SEO",
    rating: 5
  },
  {
    name: "Usman Tariq",
    role: "Brand Manager",
    review: "Our outdated website was completely transformed with modern UI/UX principles. The bounce rate decreased by 60%.",
    project: "Website Redesign",
    rating: 5
  },
  {
    name: "Sara Imran",
    role: "Restaurant Owner",
    review: "The online ordering system Momin built has doubled our takeaway orders. The interface is intuitive and beautiful.",
    project: "Restaurant Website with Ordering",
    rating: 5
  },
  {
    name: "Omar Farooq",
    role: "Educational Institute Director",
    review: "Our student portal and learning management system work flawlessly. The development process was smooth and professional.",
    project: "Educational Platform",
    rating: 5
  },
  {
    name: "Fatima Ahmed",
    role: "Healthcare Administrator",
    review: "The patient management system Momin developed has streamlined our operations significantly. Excellent technical skills!",
    project: "Healthcare Web Application",
    rating: 5
  }
];

export const Reviews = () => (
  <section id="reviews" className="bg-gradient-to-br from-gray-50 to-gray-100 text-gray-800 py-20 px-4 md:px-10 lg:px-20 relative overflow-hidden">
    {/* Animated background elements */}
    <div className="absolute top-10 left-10 w-20 h-20 bg-blue-500 rounded-full opacity-10 animate-pulse"></div>
    <div className="absolute bottom-10 right-10 w-24 h-24 bg-emerald-500 rounded-full opacity-10 animate-bounce delay-1000"></div>
    <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-blue-500 rounded-full opacity-5 animate-ping"></div>
    
    <div className="max-w-7xl mx-auto relative z-10">
      <div className="text-center mb-16">
        <motion.h2
          className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-emerald-500 text-transparent bg-clip-text inline-block mb-4"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Client Success Stories
        </motion.h2>
        <motion.p
          className="text-lg text-gray-600 max-w-3xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Discover what our clients have to say about their experience working with us on their web development projects.
        </motion.p>
      </div>

      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={30}
        breakpoints={{
          0: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          1024: { slidesPerView: 3 },
        }}
        pagination={{ 
          clickable: true,
          el: '.reviews-pagination',
          bulletClass: 'review-bullet',
          bulletActiveClass: 'review-bullet-active'
        }}
        autoplay={{ 
          delay: 5000,
          disableOnInteraction: false 
        }}
        loop={true}
        className="w-full pb-16"
      >
        {reviews.map((item, index) => (
          <SwiperSlide key={index}>
            <motion.div
              className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-blue-500 relative h-full flex flex-col transition-all duration-500 hover:shadow-2xl hover:scale-105 hover:border-emerald-500 group"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                <Quote size={60} className="text-blue-500" />
              </div>
              
              <div className="flex gap-1 mb-5">
                {[...Array(item.rating)].map((_, i) => (
                  <Star key={i} size={20} fill="currentColor" className="text-yellow-500" />
                ))}
              </div>
              
              <p className="text-gray-700 h-[100px] mb-6 text-lg leading-relaxed flex-grow group-hover:text-gray-800 transition-colors duration-300">"{item.review}"</p>
              
              <div className="mt-auto pt-4 border-t border-gray-100 group-hover:border-gray-200 transition-colors duration-300">
                <div className="font-semibold text-blue-600 text-xl group-hover:text-emerald-600 transition-colors duration-300">{item.name}</div>
                <div className="text-gray-500 group-hover:text-gray-600 transition-colors duration-300">{item.role}</div>
                <div className="text-sm text-blue-500 mt-2 font-medium group-hover:text-emerald-500 transition-colors duration-300">
                  {item.project}
                </div>
              </div>
              
              {/* Hover effect element */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-50 to-emerald-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
            </motion.div>
          </SwiperSlide>
        ))}
      </Swiper>
      
      {/* Custom pagination */}
      <div className="reviews-pagination flex justify-center gap-2 mt-6"></div>
    </div>

    <style jsx global>{`
      .review-bullet {
        width: 12px;
        height: 12px;
        background: #d1d5db;
        border-radius: 50%;
        display: inline-block;
        margin: 0 5px;
        cursor: pointer;
        transition: all 0.3s ease;
      }
      
      .review-bullet-active {
        background: linear-gradient(to right, #2563eb, #10b981);
        transform: scale(1.3);
      }
      
      .swiper-pagination {
        position: relative;
        margin-top: 2rem;
      }
    `}</style>
  </section>
);